import { isApiError } from '@pa/api'
import type { ClinicRole, TempPasswordResult, UserRow } from '@pa/api/clinic'
import { Avatar, timeAgo, useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { KeyRound, UserCog, UserMinus, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'
import { toastApiError } from '../../../errors'
import { TempPasswordDialog } from '../../../temp-password/TempPasswordDialog'
import type { UsersAdapter } from '../hooks/adapters'
import { CONFIRM_COPY, ROLE_DESCRIPTION, ROLE_LABEL, STATUS_LABEL, USERS_ERRORS, USERS_PURPOSE } from '../model/copy'
import { AddUserDialog } from './AddUserDialog'
import { ConfirmDialog } from './ConfirmDialog'

export interface UsersPageProps {
  adapter: UsersAdapter
  /** `page` (C-13: the page head with h1) or `tab` (inside S-04). */
  layout?: 'page' | 'tab'
}

type Pending = { kind: 'reset' | 'remove'; user: UserRow } | { kind: 'role'; user: UserRow; role: ClinicRole }

/** C-13b's contents: in this component's state only, until Done (Frontend §6.5). */
interface Shown {
  name: string
  email: string
  password: string
}

const other = (role: ClinicRole): ClinicRole => (role === 'admin' ? 'user' : 'admin')

/** API.md §1.6 conflict reasons of the users endpoints → the C-13 copy; null when not one of them. */
function conflictMessage(e: unknown): string | null {
  if (!isApiError(e)) return null
  if (e.code === 'not_found') return USERS_ERRORS.not_found
  if (e.code !== 'conflict') return null
  const reason = e.details?.reason
  if (reason === 'last_admin') return USERS_ERRORS.last_admin
  if (reason === 'shared_account') return USERS_ERRORS.shared_account
  if (reason === 'self_not_allowed') {
    const action = e.details?.action
    if (action === 'change_role') return USERS_ERRORS.self_change_role
    if (action === 'remove') return USERS_ERRORS.self_remove
    return USERS_ERRORS.self_reset_password
  }
  return null
}

/**
 * The shared `users` feature (Frontend §5.4, §6.5): C-13 in the clinic app (#31–#35) and the S-04 Users tab with
 * S-05 in the admin app (#47–#51). The table (Name + email, Role with its description, Status, Last active,
 * Actions), Add user (C-13a / S-05) → C-13b, and Reset password / Change role / Remove, each behind a confirm. Your
 * own row's actions are disabled (`isYou`).
 */
export function UsersPage({ adapter, layout = 'page' }: UsersPageProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const list = adapter.useList()
  const add = adapter.useAdd()
  const changeRole = adapter.useChangeRole()
  const resetPassword = adapter.useResetPassword()
  const remove = adapter.useRemove()
  const [adding, setAdding] = useState(false)
  const [pending, setPending] = useState<Pending | null>(null)
  const [shown, setShown] = useState<Shown | null>(null)
  const rows = list.data?.items ?? []

  const refresh = () => queryClient.invalidateQueries({ queryKey: adapter.listKey })

  const showPassword = (result: TempPasswordResult) => {
    if (!result.temporaryPassword) return
    setShown({ name: result.user.name, email: result.user.email, password: result.temporaryPassword })
  }

  async function confirm(p: Pending) {
    try {
      if (p.kind === 'reset') {
        const result = await resetPassword.mutateAsync({ userId: p.user.id })
        setPending(null)
        showPassword(result)
      } else if (p.kind === 'role') {
        await changeRole.mutateAsync({ userId: p.user.id, role: p.role })
        setPending(null)
        toast(`${p.user.name} is now ${p.role === 'admin' ? 'an Admin' : 'a User'}.`, 'success')
      } else {
        await remove.mutateAsync({ userId: p.user.id })
        setPending(null)
        toast(`${p.user.name} removed.`, 'success')
      }
    } catch (e) {
      setPending(null)
      const message = conflictMessage(e)
      if (message === null) toastApiError(toast, e)
      else toast(message, 'error', isApiError(e) && e.requestId ? { requestId: e.requestId } : {})
    } finally {
      void refresh()
    }
  }

  const head =
    layout === 'page' ? (
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p>{USERS_PURPOSE}</p>
        </div>
        <div className="actions">
          <AddButton
            onClick={() => {
              setAdding(true)
            }}
          />
        </div>
      </div>
    ) : (
      <div className="row users-tab-head">
        <p className="muted grow">{USERS_PURPOSE}</p>
        <AddButton
          onClick={() => {
            setAdding(true)
          }}
        />
      </div>
    )

  return (
    <>
      {head}
      <div className="card">
        <div className="table-wrap" aria-busy={list.isFetching}>
          {list.isError ? (
            <div className="empty" role="alert">
              <Users aria-hidden />
              <div>Couldn&apos;t load the users.</div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  void list.refetch()
                }}
              >
                Retry
              </button>
            </div>
          ) : list.isPending ? (
            <div className="empty" role="status">
              <span className="spinner" aria-hidden />
              <div>Loading users…</div>
            </div>
          ) : (
            <table className="table rtable users-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last active</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td className="c-patient">
                      <div className="cell-person">
                        <Avatar name={u.name} />
                        <div>
                          <b>{u.name}</b>
                          <span>{u.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="c-role" data-label="Role">
                      <b className="role-name">{ROLE_LABEL[u.role]}</b>
                      <span className="cell-sub">{ROLE_DESCRIPTION[u.role]}</span>
                    </td>
                    {/* The mockup's Two-factor column (and its phone grid area) becomes Status (UserFlows C-13). */}
                    <td className="c-2fa" data-label="Status">
                      <span className={`pill ${u.status === 'active' ? 'pill-active' : 'pill-expired'}`}>
                        {STATUS_LABEL[u.status]}
                      </span>
                    </td>
                    <td className="muted c-last" data-label="Last active">
                      {u.lastActiveAt ? timeAgo(u.lastActiveAt) : 'Never'}
                    </td>
                    <td className="c-actions">
                      <RowActions
                        user={u}
                        onReset={() => {
                          setPending({ kind: 'reset', user: u })
                        }}
                        onRole={() => {
                          setPending({ kind: 'role', user: u, role: other(u.role) })
                        }}
                        onRemove={() => {
                          setPending({ kind: 'remove', user: u })
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {adding && (
        <AddUserDialog
          defaultRole={adapter.defaultRole}
          add={add}
          onClose={() => {
            setAdding(false)
            void refresh()
          }}
          onTemporaryPassword={(result) => {
            setAdding(false)
            showPassword(result)
            void refresh()
          }}
        />
      )}

      {pending && (
        <ConfirmDialog
          {...confirmCopy(pending)}
          danger={pending.kind === 'remove'}
          pending={resetPassword.isPending || changeRole.isPending || remove.isPending}
          onCancel={() => {
            setPending(null)
          }}
          onConfirm={() => void confirm(pending)}
        />
      )}

      {shown && (
        <TempPasswordDialog
          name={shown.name}
          email={shown.email}
          password={shown.password}
          onDone={() => {
            // Frontend §6.5: drop the password from state and from the mutation cache.
            setShown(null)
            add.reset()
            resetPassword.reset()
          }}
        />
      )}
    </>
  )
}

function confirmCopy(p: Pending): { title: string; body: string; action: string } {
  if (p.kind === 'reset') {
    return {
      title: CONFIRM_COPY.reset.title(p.user.name),
      body: CONFIRM_COPY.reset.body,
      action: CONFIRM_COPY.reset.action,
    }
  }
  if (p.kind === 'role') {
    return {
      title: CONFIRM_COPY.role.title(p.user.name, p.role),
      body: CONFIRM_COPY.role.body(p.role),
      action: CONFIRM_COPY.role.action,
    }
  }
  return {
    title: CONFIRM_COPY.remove.title(p.user.name),
    body: CONFIRM_COPY.remove.body,
    action: CONFIRM_COPY.remove.action,
  }
}

function AddButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn btn-primary" onClick={onClick}>
      <UserPlus aria-hidden /> Add user
    </button>
  )
}

/** Reset password, Change role and Remove; disabled on your own row (Frontend §6.1 C-13 row: `isYou`). */
function RowActions({
  user,
  onReset,
  onRole,
  onRemove,
}: {
  user: UserRow
  onReset: () => void
  onRole: () => void
  onRemove: () => void
}) {
  const own = user.isYou
  const title = (label: string) => (own ? `${label} (not on your own account)` : label)
  return (
    <div className="row-actions">
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        aria-label={`Reset password for ${user.name}`}
        title={title('Reset password')}
        disabled={own}
        onClick={onReset}
      >
        <KeyRound aria-hidden />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        aria-label={`Change role for ${user.name}`}
        title={title('Change role')}
        disabled={own}
        onClick={onRole}
      >
        <UserCog aria-hidden />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        aria-label={`Remove ${user.name}`}
        title={title('Remove')}
        disabled={own}
        onClick={onRemove}
      >
        <UserMinus aria-hidden />
      </button>
    </div>
  )
}
